
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { videoService } from "@/services/video.service"
import { Video } from "@/types/api"
import { Button } from "@/components/ui/button"
import { Trash2, Plus, RefreshCw, PlayCircle, Loader2, Pencil } from "lucide-react"
import { UploadVideoDialog } from "@/components/videos/UploadVideoDialog"
import { VideoEditDialog } from "@/components/videos/VideoEditDialog"
import { useToast } from "@/hooks/use-toast"
import { Badge } from "@/components/ui/badge"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function VideosPage() {
    const [videos, setVideos] = useState<Video[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [editVideo, setEditVideo] = useState<Video | null>(null)
    const [deleteVideoId, setDeleteVideoId] = useState<number | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const PAGE_SIZE = 10

    const fetchVideos = async () => {
        setIsLoading(true)
        try {
            const response = await videoService.getVideos({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            setVideos(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить видео",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchVideos()
    }, [page])

    const handleDelete = async () => {
        if (!deleteVideoId) return
        setIsDeleting(true)
        try {
            await videoService.deleteVideo(deleteVideoId)
            toast({
                title: "Успешно",
                description: "Видео успешно удалено",
            })
            fetchVideos()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить видео",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
            setDeleteVideoId(null)
        }
    }

    const columns: Column<Video>[] = [
        {
            header: "Миниатюра",
            cell: (video) => {
                const thumbnailUrl = video.custom_thumbnail || video.poster_url || video.thumbnail_url
                return (
                    <div className="h-10 w-16 overflow-hidden rounded bg-gray-100 flex items-center justify-center">
                        {thumbnailUrl ? (
                            <img src={thumbnailUrl} alt={video.title} className="h-full w-full object-cover" />
                        ) : (
                            <PlayCircle className="h-6 w-6 text-gray-300" />
                        )}
                    </div>
                )
            },
        },
        {
            header: "Название",
            accessorKey: "title",
            cell: (video) => (
                <div>
                    <div className="font-medium">{video.title}</div>
                    <div className="text-xs text-gray-500">{video.original_filename}</div>
                </div>
            )
        },
        {
            header: "Статус",
            accessorKey: "status",
            cell: (video) => {
                const colors: Record<string, string> = {
                    ready: "bg-green-100 text-green-700",
                    processing: "bg-yellow-100 text-yellow-700",
                    uploading: "bg-blue-100 text-blue-700",
                    failed: "bg-red-100 text-red-700",
                    pending: "bg-gray-100 text-gray-700",
                }
                return (
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${colors[video.status] || colors.pending}`}>
                        {video.status}
                    </span>
                )
            }
        },
        {
            header: "Длительность",
            accessorKey: "duration_formatted",
        },
        {
            header: "Создано",
            cell: (video) => new Date(video.created_at).toLocaleDateString(),
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (video) => (
                <div className="flex justify-end gap-2">
                    {/* Refresh status button for processing videos */}
                    {video.status !== 'ready' && video.status !== 'failed' && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                                await videoService.getVideoStatus(video.id, true)
                                fetchVideos()
                            }}
                            title="Проверить статус"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditVideo(video)}
                        title="Редактировать"
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setDeleteVideoId(video.id)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Видео</h1>
                    <p className="text-gray-600 mt-1">Управление видеоконтентом в VdoCipher.</p>
                </div>
                <Button onClick={() => setIsDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Загрузить видео
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список видео</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={videos}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <UploadVideoDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSuccess={fetchVideos}
            />

            <VideoEditDialog
                open={!!editVideo}
                onOpenChange={(open) => !open && setEditVideo(null)}
                video={editVideo}
                onSuccess={fetchVideos}
            />

            <AlertDialog open={!!deleteVideoId} onOpenChange={(open) => !open && setDeleteVideoId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это действие нельзя отменить. Это навсегда удалит видео из VdoCipher.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
